import { validateEmail } from "@services/validateEmail/validateEmail";

const WORKSPACE_CODE = process.env.PRODUCT_FRUITS_WORKSPACE_CODE;
const LOADER_URL = "https://app.productfruits.com/static/script.js";
// Mirrors the fallbackLng of src/services/i18n, so a user with no language set
// does not get tours in another language than the UI around them.
const FALLBACK_LANGUAGE = "en";

// While an instance is running: the language it was booted with and the last
// payload pushed. Product Fruits keeps its boot language, so switching language
// means tearing the instance down before booting it again.
let booted = null;

// This integration stays silent in five different situations, which is hard to
// tell apart from it being broken. Outside production it says which one it took.
// ENVIRONMENT is only defined by the prod webpack config, so this is on in dev.
const log = (...args) => {
	if (process.env.ENVIRONMENT !== "production") {
		console.info("[productFruits]", ...args);
	}
};

const injectLoader = () => {
	window.$productFruits = window.$productFruits || [];
	window.productFruits = window.productFruits || {};
	window.productFruits.scrV = "2";

	// destroy() leaves the tag in place, so a reboot must not append a second one.
	if (document.head.querySelector(`script[src="${LOADER_URL}"]`)) {
		return;
	}

	const script = document.createElement("script");
	script.async = true;
	script.src = LOADER_URL;
	document.head.appendChild(script);
};

const findWorkspaceRole = (auth, workspaceId) =>
	auth.roles?.find(
		role => role?.workspace?.id && String(role.workspace.id) === String(workspaceId)
	);

// No field is left out on purpose: Product Fruits merges what it receives, so an
// omitted key would keep whatever was identified before it.
const buildUserInfo = (auth, workspaceId, currentRole) => ({
	// Same identifier as the one already sent to Crisp, OpenReplay and Sentry,
	// so a user can be followed across the four tools.
	username: auth.email,
	email: validateEmail(auth.email) ? auth.email : "",
	firstname: auth.surname || "",
	lastname: auth.lastname || "",
	role: currentRole?.name || "",
	props: {
		app: "priceit",
		isRoot: !!auth.isRoot,
		function: auth.function || "",
	},
	// Workspaces are the account-level grain, which is how tours and surveys
	// get targeted at a customer rather than at a single seat.
	group: { groupId: String(workspaceId) },
});

export const destroyProductFruits = () => {
	if (!booted) {
		return;
	}

	if (window.productFruitsIsReady) {
		window.productFruits?.services?.destroy?.();
	} else {
		// The loader has not run yet, so destroy() would be a no-op and the queued
		// init would still boot the widget as this user after they logged out.
		window.$productFruits = [];
	}

	booted = null;
	log("torn down");
};

export const syncProductFruitsUser = (auth, workspaceId) => {
	if (!WORKSPACE_CODE) {
		log("off: PRODUCT_FRUITS_WORKSPACE_CODE is not set in this build");
		return;
	}

	if (!auth?.email || !workspaceId) {
		log("waiting: no authenticated user inside a workspace yet");
		return;
	}

	// This runs from an effect fed by the raw URL parameter, before the route has
	// decided whether the user may see that workspace. Identifying them into one
	// they hold no role in would create a phantom group in Product Fruits.
	const currentRole = findWorkspaceRole(auth, workspaceId);
	if (!currentRole && !auth.isRoot) {
		log(`skipped: ${auth.email} holds no role in workspace ${workspaceId}`);
		return;
	}

	const language = (auth.language || FALLBACK_LANGUAGE).toLowerCase();
	const userInfo = buildUserInfo(auth, workspaceId, currentRole);
	const signature = JSON.stringify(userInfo);

	if (booted?.language !== language) {
		destroyProductFruits();
		injectLoader();
		window.$productFruits.push(["init", WORKSPACE_CODE, language, userInfo]);
		booted = { language, signature };
		log(`booting in "${language}" as ${userInfo.username}, workspace ${workspaceId}`);
		return;
	}

	if (booted.signature !== signature) {
		booted.signature = signature;
		window.productFruits?.identifyUser?.(userInfo);
		log(`re-identified ${userInfo.username}, workspace ${workspaceId}`);
	}
};
